import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { User, Lock, Eye, EyeOff, AlertCircle, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { setRememberMe, setToken } from '../utils/tokenStorage';
import { GoogleSignInButton } from '../components/GoogleSignInButton';

export default function Login() {
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [rememberMe, setRememberMeState] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.identifier.trim()) {
      setError('Please enter your username or email');
      return;
    }

    if (!formData.password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/auth/login', {
        identifier: formData.identifier.trim().toLowerCase(),
        password: formData.password
      });

      const userData = response.data;
      
      // Set remember me preference BEFORE storing token
      setRememberMe(rememberMe);
      
      // Store session token in correct storage
      setToken(userData.session_token);
      
      // Update auth store
      setUser({
        user_id: userData.user_id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        role: userData.role,
        username: userData.username
      });

      toast.success('Welcome back!');
      const params = new URLSearchParams(window.location.search);
      navigate(params.get('redirect') || '/dashboard', { replace: true });
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to sign in. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <Card className="w-full max-w-md glass-default border-white/10">
        <CardHeader className="text-center pb-2">
          <h1 className="text-3xl font-bold gradient-text">Welcome Back</h1>
          <p className="text-white/60 mt-2">Sign in to your account</p>
        </CardHeader>

        <CardContent className="space-y-6 pt-4">
          {/* Error Display */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-white flex items-center gap-2">
                <User size={16} />
                Username or Email
              </Label>
              <Input
                id="identifier"
                name="identifier"
                type="text"
                placeholder="Enter username or email"
                value={formData.identifier}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-violet-500 h-12"
                data-testid="login-identifier-input"
                required
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white flex items-center gap-2">
                <Lock size={16} />
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 focus:border-violet-500 h-12 pr-12"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Stay Logged In Checkbox */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setRememberMeState(!rememberMe)}
                className={`w-5 h-5 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                  rememberMe 
                    ? 'bg-violet-600 border-violet-500' 
                    : 'bg-white/5 border-white/20 hover:border-white/40'
                }`}
                data-testid="remember-me-checkbox"
              >
                {rememberMe && <Check size={14} className="text-white" />}
              </button>
              <span 
                className="text-sm text-white/60 cursor-pointer select-none"
                onClick={() => setRememberMeState(!rememberMe)}
              >
                Stay logged in until I logout
              </span>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-lg font-bold rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <GoogleSignInButton label="Sign in with Google" />
        </CardContent>

        <CardFooter className="flex flex-col gap-4 pt-0">
          <div className="text-center">
            <p className="text-white/60">
              Don&apos;t have an account?{' '}
              <Link to="/signup" className="text-violet-400 hover:underline font-medium">
                Create Account
              </Link>
            </p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
